import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Stack, Button, Card, List, ListItem, ListItemText, Divider, Typography, CircularProgress, Box } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { getAllFoods, updateFoodPriority } from '../../../redux/slices/stockManagementRedux/foodRedux';

const selectFoods = (state) => state?.food?.allData || [];

const buildList = (allData, filterType) =>
  (allData || [])
    .filter((v) => !String(v?.name || '').toLowerCase().includes('delete') && (filterType ? v?.type === filterType : ['ingredient', 'precooked'].includes(v?.type)))
    .map((v) => ({ id: v.id, name: v.name, priority: Number.isFinite(Number(v?.priority)) ? Number(v.priority) : null }))
    .sort((a, b) => {
      const pa = a.priority ?? Infinity;
      const pb = b.priority ?? Infinity;
      if (pa !== pb) return pa - pb;
      return String(a.name).localeCompare(b.name);
    });

const toCompact = (arr = []) => arr.map(({ id, name, priority }) => ({ id, name, priority }));

const payloadFromOrder = (items) => items.map((v, idx) => ({ id: v.id, priority: idx + 1 }));

const Row = ({ id, name, isMoved }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <ListItem
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        px: 1.25,
        py: 1,
        my: 0.5,
        borderRadius: 1.5,
        cursor: 'grab',
        bgcolor: isDragging ? 'action.hover' : isMoved ? 'success.light' : 'background.paper',
        color: isMoved ? 'success.contrastText' : 'text.primary',
        boxShadow: isMoved ? 2 : 0,
        border: 1,
        borderColor: isMoved ? 'success.main' : 'divider',
        opacity: isDragging ? 0.85 : 1,
        transition: 'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease'
      }}
      style={style}
    >
      <ListItemText primaryTypographyProps={{ noWrap: true, fontSize: 14, fontWeight: 600 }} primary={`${id} — ${name}`} />
    </ListItem>
  );
};

const FooterBar = ({ onSave, onReset, dirty, saving }) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button onClick={onReset} startIcon={<RestoreIcon />} disabled={!dirty || saving} variant="outlined" size="small">
        Reset
      </Button>
      <Button onClick={onSave} startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} disabled={!dirty || saving} variant="contained" size="small">
        Save Order
      </Button>
    </Stack>
  );
};

const FoodPriority = () => {
  const { type } = useParams();
  const { themeStretch } = useSettings();
  const allData = useSelector(selectFoods);
  const initial = useMemo(() => buildList(allData, type), [allData, type]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [movedIds, setMovedIds] = useState(new Set());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const dirty = useMemo(() => JSON.stringify(toCompact(items)) !== JSON.stringify(toCompact(initial)), [items, initial]);

  useEffect(() => {
    dispatch(getAllFoods());
  }, []);

  useEffect(() => {
    setItems(initial);
    setMovedIds(new Set());
  }, [initial]);

  const handleDragEnd = (event) => {
    const { active, over } = event || {};
    if (!over || active?.id === over?.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    setItems((arr) => arrayMove(arr, oldIndex, newIndex));
    setMovedIds((prev) => {
      const next = new Set(prev);
      next.add(active.id);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const body = { type, items: payloadFromOrder(items) };
      dispatch(updateFoodPriority(body));
      setSaving(false);
      setMovedIds(new Set());
    } catch (e) {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setItems(initial);
    setMovedIds(new Set());
  };

  return (
    <Page title="Food Priority">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Food Priority"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: '' }, { name: 'Food Priority' }]}
          action={<FooterBar onSave={handleSave} onReset={handleReset} dirty={dirty} saving={saving} />}
        />
        <Box sx={{ height: 'calc(100vh - 25vh)' }}>
          <Card sx={{ p: 1.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ px: 0.5, pb: 0.75, display: 'block', opacity: 0.7 }}>
              Drag to set priority. Top = Highest. Moved rows are highlighted.
            </Typography>
            <Divider />
            <Box sx={{ flex: 1, minHeight: 0, pt: 0.5 }}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                  <List dense disablePadding sx={{ height: '100%', overflowY: 'auto' }}>
                    {items.map((item) => (
                      <Row key={item.id} id={item.id} name={item.name} isMoved={movedIds.has(item.id)} />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            </Box>
          </Card>
        </Box>
      </Container>
    </Page>
  );
};

export default FoodPriority;
